import React, { useRef, useEffect, useState } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import AnimatedSection from "../common/AnimatedSection";
import SectionHeading from "../common/SectionHeading";

const NAVY = "#1B1F3B"; const CORAL = "#FF6B6B"; const YELLOW = "#FFD93D"; const TEAL = "#06D6A0"; const PURPLE = "#845EF7";

const useCounter = (target, inView) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 25);
    return () => clearInterval(timer);
  }, [inView, target]);
  return count;
};

const StatCard = ({ stat, color, inView }) => {
  const count = useCounter(stat.value, inView);
  return (
    <Box sx={{ p: 3, textAlign: "center", borderRadius: "16px", border: `2.5px solid ${NAVY}`, backgroundColor: color, boxShadow: `5px 5px 0 ${NAVY}`, transition: "all 0.2s", "&:hover": { transform: "translate(-2px,-2px)", boxShadow: `8px 8px 0 ${NAVY}` } }}>
      <Typography sx={{ fontFamily: "'Fredoka One', cursive", fontSize: { xs: "3rem", md: "3.8rem" }, color: NAVY, lineHeight: 1 }}>
        {count}{stat.suffix}
      </Typography>
      <Typography sx={{ color: NAVY, fontWeight: 800, fontSize: "0.85rem", mt: 1 }}>{stat.label}</Typography>
    </Box>
  );
};

const AchievementsSection = ({ stats, achievements }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const COLORS = [TEAL, YELLOW, CORAL, PURPLE];

  return (
    <AnimatedSection sx={{ backgroundColor: "#FFF8F0", py: { xs: 9, md: 12 } }} ref={ref}>
      <Box ref={ref}>
        <Container maxWidth="xl">
          <SectionHeading
            eyebrow="Our Impact"
            title="Growing Together, Achieving More 🏆"
            description="Every achievement reflects our students' growth, learning and success, supported by our teachers and school community."
            align="center"
            eyebrowColor={PURPLE}
            eyebrowBg="#EDE7FF"
          />

          <Grid container spacing={3} sx={{ mb: 7 }}>
            {stats.map((s, i) => (
              <Grid item xs={6} md={3} key={i}>
                <StatCard stat={s} color={COLORS[i % COLORS.length]} inView={inView} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            {achievements.map((a, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box sx={{ p: 3.5, height: "100%", borderRadius: "16px", border: `2.5px solid ${NAVY}`, backgroundColor: "#fff", boxShadow: `5px 5px 0 ${NAVY}`, transition: "all 0.2s", "&:hover": { transform: "translate(-2px,-2px)", boxShadow: `8px 8px 0 ${NAVY}` } }}>
                  <Box sx={{ fontSize: "2rem", mb: 1.5, animation: `bounce ${2 + i * 0.4}s ease-in-out infinite` }}>
                    {["🥇", "🎤", "📈"][i]}
                  </Box>
                  <Typography sx={{ fontFamily: "'Fredoka One', cursive", fontSize: "1.2rem", color: NAVY, mb: 1 }}>{a.title}</Typography>
                  <Typography sx={{ color: "#4A4E6D", fontWeight: 600, lineHeight: 1.8, fontSize: "0.9rem" }}>{a.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </AnimatedSection>
  );
};

export default AchievementsSection;
